import { getExportedName } from "./exported-names.js";
import { getBaseUrl } from "./dynamic-env.js";
import { setDependencyGlobals } from "./external-dependencies.js";

/**
 * Does the main work to load in a dynamic component
 * @param {{}} opts options
 * @param {string} opts.name name of the dynamic export to load
 * @param {beforeLoad} [opts.beforeLoad] beforeLoad hook
 * @param {afterLoad} [opts.afterLoad] afterLoad hook
 * @param {Window} [opts.globalThis] the global window object - parameterized to make unit testing possible
 * @return {DynamicExport} exported value
 */
export default async function dynamicWrapperFactory({
  name,
  beforeLoad = () => {},
  afterLoad = () => {},
  globalThis,
}) {
  if (!globalThis) globalThis = window;

  setDependencyGlobals(globalThis);

  await beforeLoad();

  const baseUrl = getBaseUrl();

  await loadScript(baseUrl + "exports/" + name + ".js", globalThis);

  const exported = globalThis[getExportedName(name)];

  await afterLoad(exported);

  return exported;
}

/**
 * Load a script asynchronously
 * @param {string} url url to load
 * @param {Window} globalThis window in which to load the script
 * @return {PromiseLike} promise which resolves or rejects when the script loads
 */
export function loadScript(
  url,
  /* istanbul ignore next */
  globalThis = window
) {
  return new Promise((resolve, reject) => {
    const script = globalThis.document.createElement("script");
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;
    globalThis.document.head.appendChild(script);
  });
}

/**
 * Called before script is loaded
 * @callback beforeLoad
 */

/**
 * Called after script is loaded
 * @callback afterLoad
 * @param {DynamicExport} exported
 */

/**
 * Dynamically-exported value
 * @typedef {*} DynamicExport
 */
